import * as React from 'react';
import moment from 'moment';
import { AxiosResponse } from 'axios';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { Popup } from 'semantic-ui-react';
import './side-screen.scss';
import Label from 'common/label/label';

// Assets
import { ReactComponent as ActionIcon } from 'assets/icons/action.svg';
import { ReactComponent as Close } from 'assets/images/close.svg';
import { ReactComponent as PinSolidLargeIcon } from 'assets/icons/pin_solid_large.svg';
import { ReactComponent as PinSolidLargeActiveIcon } from 'assets/icons/pin_solid_large_active.svg';
import { ReactComponent as CloseImageIcon } from 'assets/icons/close_image.svg';
import { ReactComponent as ImageInputSvg } from 'assets/icons/image_input.svg';
import { INoticeCommentItem, INoticeItem } from 'types/notice';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useUserStore from 'zustands/useUserStore';

const popupStyle = {
  borderRadius: 8,
  width: '102px',
  height: '84px',
  border: '1px solid #E9E9E9',
  boxShadow: '2px 2px 20px rgba(0, 0, 0, 0.06)',
};

interface Props {
  onClose: () => void;
  selectNotice: INoticeItem | undefined;
  onCommentUpdate: () => void;
}

const SideScreen: React.FunctionComponent<Props> = ({ onClose, selectNotice, onCommentUpdate }) => {
  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { userName } = useUserStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [commentList, setCommentList] = React.useState<INoticeCommentItem[]>([]);
  const [commentEditValue, setCommentEditValue] = React.useState<string>('');
  const [commentValue, setCommentValue] = React.useState<string>('');

  const fetchComment = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getNoticeComment`, {
        NOTICE_SEQ: selectNotice?.NOTICE_SEQ,
      });
      console.log('/auth/getNoticeComment_data', data);

      if (data.resultCode === 0) {
        setCommentList(data.resultData.map((i: INoticeCommentItem) => ({ ...i, editMode: false })));
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/getNoticeComment');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 본인 댓글 수정
  const editMineComment = (com: INoticeCommentItem) => {
    const formatNoticeItem = commentList.map((i) => ({ ...i, editMode: i.COM_SEQ === com.COM_SEQ ? true : false }));
    setCommentList(formatNoticeItem);
    setCommentEditValue(com.CONTENTS);
  };

  // 댓글 수정 취소
  const handleEditCancel = () => {
    const formatNoticeItem = commentList.map((i) => ({ ...i, editMode: false }));
    setCommentList(formatNoticeItem);
  };

  // 댓글 수정 인풋 상태 핸들러
  const handleCommentEditInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setCommentEditValue(value);
  };

  // 댓글 추가 인풋 상태 핸들러
  const handleCommentInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setCommentValue(value);
  };

  // 새 댓글 등록
  const submitComment = async () => {
    if (commentValue === '0') {
      return setModalState({
        isModalShow: true,
        title: '댓글 등록',
        desc: '등록하실 댓글 내용을 입력해 주세요',
      });
    }

    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/setNoticeComment`, {
        NOTICE_SEQ: selectNotice?.NOTICE_SEQ,
        CONTENTS: commentValue,
        STORE: selectNotice?.STORE,
        EMP_NAME: userName,
      });
      console.log('/auth/setNoticeComment_data', data);

      if (data.resultCode === 0) {
        setCommentValue('');
        fetchComment();
        onCommentUpdate();
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/setNoticeComment');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 나의 댓글 삭제
  const deleteMineComment = async (com: INoticeCommentItem) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/delNoticeComment`, {
        COM_SEQ: com.COM_SEQ,
      });
      console.log('/auth/delNoticeComment_data', data);

      if (data.resultCode === 0) {
        fetchComment();
        onCommentUpdate();
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/delNoticeComment');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 댓글 수정하기
  const editComment = async (comSeq: number) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/editNoticeComment`, {
        COM_SEQ: comSeq,
        CONTENTS: commentEditValue,
      });
      console.log('/auth/editNoticeComment_data', data);

      if (data.resultCode === 0) {
        fetchComment();
        onCommentUpdate();
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/editNoticeComment');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const inputFile = React.useRef(null);

  const handleAddImage = () => {
    (inputFile as any).current.click();
  };

  React.useEffect(() => {
    fetchComment();
  }, []);

  const imgArr = selectNotice?.IMG_LIST?.split('@') ?? [];

  return (
    <div className="side-screen">
      <div className="side-screen-bg" onClick={onClose} />
      <div className="side-screen-content">
        <div className="side-screen-header">
          <div className="side-screen-action">
            <div className="side-screen-action__close" onClick={onClose}>
              <Close />
            </div>
          </div>
          <div className="side-screen-header__label">
            {selectNotice?.STORE === '1' ? (
              <Label text="지시사항" type="custom" color_type="#49AE9E" textColor="#FFFFFF" />
            ) : (
              <Label text="특이사항" type="custom" color_type="#E47C77" textColor="#FFFFFF" />
            )}
          </div>
          <div className="side-screen-header__head">
            <div className="side-screen-header__pin">
              {selectNotice?.favorite ? <PinSolidLargeActiveIcon /> : <PinSolidLargeIcon />}
            </div>

            <div className="side-screen-header__text">{selectNotice?.TITLE}</div>
          </div>
          <div className="side-screen-body">{selectNotice?.CONTENTS}</div>
          {imgArr.length > 0 && (
            <div className="side-screen-body">
              <div className="imgList">
                <PhotoProvider>
                  {imgArr.map((i, _i) => {
                    console.log('PhotoProvider i', i);
                    return (
                      <div key={i} style={{ marginLeft: 10, cursor: 'pointer' }}>
                        <PhotoView src={i} key={i}>
                          <img src={i} alt={_i.toString()} width={100} height={100} />
                        </PhotoView>
                      </div>
                    );
                  })}
                </PhotoProvider>
              </div>
            </div>
          )}
          <div className="side-screen-body__footer">
            <div></div>
            <div className="side-screen-body__footer__profile-name">{selectNotice?.EMP_NAME}</div>
            <div className="side-screen-body__footer__vertical">|</div>
            {moment(selectNotice?.ADDDATE).isValid() && (
              <div className="side-screen-body__footer__date">{moment(selectNotice?.ADDDATE).format('YYYY.MM.DD')}</div>
            )}
          </div>
        </div>
        <div className="side-screen-comments__scroll">
          {commentList.length > 0 &&
            commentList.map((com) => (
              <div
                key={com.COM_SEQ.toString()}
                className="side-screen-comments"
                style={{ padding: com.editMode ? 20 : 35 }}>
                <div className={com.editMode ? 'side-screen-comments__border' : ''}>
                  <div className="side-screen-comments__head">
                    <img
                      src="https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
                      alt="image"
                      className="side-screen-comments__head__profileImage"
                    />
                    <div className="side-screen-comments__head__head">
                      <div className="side-screen-comments__head__text">{com.EMP_NAME}</div>
                      <div className="side-screen-comments__head__subtext">
                        {moment(com.CREATE_TIME).format('YYYY.MM.DD HH:mm')} 작성
                      </div>
                    </div>
                    {/* 본인 댓글만 */}
                    {com.self === '1' && !com.editMode && (
                      <div>
                        <Popup
                          on="click"
                          trigger={
                            <div className="checklist__popup__icon">
                              <ActionIcon />
                            </div>
                          }
                          position="bottom left"
                          style={popupStyle}
                          basic
                          flowing
                          hoverable>
                          <div className="checklist__popups">
                            <div
                              className="checklist__popup__resend-invite"
                              onClick={() => {
                                editMineComment(com);
                              }}>
                              수정
                            </div>
                            <div
                              className="checklist__popup__delete-invite"
                              onClick={() => {
                                setModalState({
                                  isModalShow: true,
                                  title: '댓글 삭제',
                                  desc: '해당 댓글을 삭제하시겠습니까?',
                                  okBtnText: '확인',
                                  okCallback: () => deleteMineComment(com),
                                  isWarning: true,
                                });
                              }}>
                              삭제
                            </div>
                          </div>
                        </Popup>
                      </div>
                    )}
                  </div>
                  {!com.editMode ? (
                    <div className="side-screen-comments__content">{com.CONTENTS}</div>
                  ) : (
                    <textarea
                      name="editText"
                      className="side-screen-comments__content__edit"
                      value={commentEditValue}
                      onChange={handleCommentEditInput}
                      rows={4}
                    />
                  )}
                  {com.editMode && (
                    <div className="side-screen-comments__edit-actions">
                      <div className="side-screen-comments__edit-actions__cancel" onClick={handleEditCancel}>
                        취소
                      </div>
                      <div
                        className="side-screen-comments__edit-actions__save"
                        onClick={() => editComment(com.COM_SEQ)}>
                        저장
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>

        <div className="side-screen-add-comment">
          <textarea className="side-screen-add-comment__input" value={commentValue} onChange={handleCommentInput} />
          <div className="side-screen-add-comment__input__icon" onClick={submitComment}>
            등록
          </div>
        </div>
      </div>
    </div>
  );
};
export default SideScreen;
